import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import kebabCase from "lodash/kebabCase";
import { withTheme } from "shared/context/theme";
import { buildQAHook } from "shared/utils";
import { elementClicked } from "shared/utils/cohesion";
import Link from "shared/components/Link";
import debounce from "lodash/debounce";
import Submenu from "./Submenu";
import styles from "./styles.css";
import themes from "./themes";

export const isTouchingDelay = 1000; // ms

class TextItem extends PureComponent {
  constructor(props) {
    super(props);

    this.submenu = React.createRef();
    this.state = {
      isSubmenuOpen: false,
      isTouching: false,
    };
  }

  setIsTouchingFalse = debounce(
    () => this.setState({ isTouching: false }),
    isTouchingDelay
  );

  getIsActive = () => {
    if (typeof window === "undefined") return false;
    const { url } = this.props;

    // Item is active when current location & item's URL match
    return !!url && !!window.location.href.match(url);
  };

  handleAnchorClick = (event) => {
    const { title, url, onClick, submenu } = this.props;
    const { isTouching } = this.state;

    if (typeof onClick === "function") {
      onClick(event);
    }

    elementClicked({
      actionOutcome: "Internal Link",
      outboundUrl: url,
      webElement: {
        location: "Header",
        elementType: "Link",
        position: "Global Nav Bar",
        text: title,
      },
    });

    if (isTouching && submenu) {
      /**
       * Don't follow the link on tablet devices (mobile has a different layout
       * & menu) if there's a submenu to display. Note that this only applies to
       * items with a url prop. Otherwise TextItem renders a span without a
       * click handler anyway.
       */
      event.preventDefault();
    }
  };

  handleItemTouchStart = () => {
    this.setState({ isTouching: true });
  };

  handleDocumentTouchEnd = (event) => {
    document.removeEventListener("touchend", this.handleDocumentTouchEnd);

    // Emulates "mouseleave" behaviour for touch devices and
    // lets click events from inside the Subnav go through.
    if (!this.submenu.current.contains(event.target)) {
      this.closeSubmenu();
    }
  };

  handleItemTouchEnd = () => {
    const { submenu } = this.props;
    const { isSubmenuOpen } = this.state;

    if (submenu && !isSubmenuOpen) {
      document.addEventListener("touchend", this.handleDocumentTouchEnd);
      this.openSubmenu();
    }

    this.setIsTouchingFalse();
  };

  handleItemMouseEnter = () => {
    const { submenu } = this.props;

    if (submenu) this.openSubmenu();
  };

  handleItemMouseLeave = () => {
    const { submenu } = this.props;

    if (submenu) this.closeSubmenu();
  };

  openSubmenu = () => {
    this.setState({ isSubmenuOpen: true });
  };

  closeSubmenu = () => {
    this.setState({ isSubmenuOpen: false });
  };

  render() {
    const {
      theme,
      title,
      url,
      isExternal,
      children,
      submenu,
      className,
      activeClassName,
    } = this.props;

    const { isSubmenuOpen } = this.state;
    const isActive = this.getIsActive();
    const id = `${kebabCase(title)}-menu-desktop`;
    const label = `${kebabCase(title)}-heading-desktop`;
    const content = children || title;

    return (
      <div
        className={cn({
          [theme.styles.item]: true,
          [theme.styles.isSubmenuOpen]: isSubmenuOpen,
          [activeClassName || theme.styles.isActive]: isActive,
          [className]: className,
        })}
        id={label}
        aria-controls={submenu ? id : null}
        data-lpa-category="Destinations Next"
        data-lpa-action="Global Navigation Click"
        data-lpa-label={title}
        data-qa={buildQAHook(title, "desktop", "div")}
        onTouchStart={this.handleItemTouchStart}
        onTouchEnd={this.handleItemTouchEnd}
        onMouseEnter={this.handleItemMouseEnter}
        onMouseLeave={this.handleItemMouseLeave}
        role="presentation"
      >
        {url ? (
          <Link
            href={url}
            className={theme.styles.link}
            onClick={this.handleAnchorClick}
            isExternal={isExternal}
          >
            {content}
          </Link>
        ) : (
          <span className={theme.styles.link}>{content}</span>
        )}
        {submenu && (
          <Submenu
            {...submenu}
            id={id}
            className={theme.styles.submenu}
            ref={this.submenu}
            aria-hidden={isSubmenuOpen ? "false" : "true"}
            aria-labelledby={label}
          />
        )}
      </div>
    );
  }
}

TextItem.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  url: PropTypes.string,
  isExternal: PropTypes.bool,
  theme: PropTypes.shape({
    styles: PropTypes.shape({}).isRequired,
  }).isRequired,
  submenu: PropTypes.shape({}),
  children: PropTypes.element,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

TextItem.defaultProps = {
  submenu: null,
  children: null,
  className: "",
  onClick: null,
  url: null,
  isExternal: false,
};

export default withTheme(TextItem, styles, themes);
